import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VerifiedIcon from '@mui/icons-material/Verified';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { platformHubUrl } from '../../core/config';
import { ROUTES } from '../../app/routes';
import { useAppSelector } from '../../app/store';
import { hubApi, write } from '../hub/hubApi';
import type { JobCard, OrganizationCard, ProductCard } from './publicApi';
import { companyPath, employmentLabel, formatMoney, openCompany, usePublicLanguage } from './components';

/*
 * Image-first cards for the explore landing: no card surface, a squarer photo with a category
 * badge and a heart, text underneath. Businesses without a cover get a tinted panel in their
 * group colour (see .hub-xcard-media[data-group] in public.css) with the logo or initials.
 */

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');
}

/** An image that falls back to `fallback` when the URL is missing or fails to load. */
function Photo({ src, alt = '', className, fallback }: { src: string | null | undefined; alt?: string; className?: string; fallback: ReactNode }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <>{fallback}</>;
  return <img className={className} src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

// ---- favourites: loaded once per page, shared by every heart ----

interface Favourites { has: (organizationId: string) => boolean; toggle: (organizationId: string) => void }
const FavouritesContext = createContext<Favourites | null>(null);

export function FavouritesProvider({ children }: { children: ReactNode }) {
  const signedIn = useAppSelector((state) => state.session.status === 'authenticated');
  const navigate = useNavigate();
  const [ids, setIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!signedIn) { setIds(new Set()); return undefined; }
    const controller = new AbortController();
    hubApi.favourites(controller.signal)
      .then((cards) => setIds(new Set(cards.map((card) => card.organizationId))))
      .catch(() => undefined);
    return () => controller.abort();
  }, [signedIn]);

  const toggle = useCallback((organizationId: string) => {
    if (!signedIn) { navigate(ROUTES.login, { state: { from: ROUTES.personalFavourites } }); return; }
    const saved = ids.has(organizationId);
    // Optimistic: flip now, undo if the server refuses.
    const flip = (on: boolean) => setIds((current) => {
      const next = new Set(current);
      if (on) next.add(organizationId); else next.delete(organizationId);
      return next;
    });
    flip(!saved);
    write(saved ? `/me/favourites/${organizationId}` : '/me/favourites', saved ? null : { organizationId }, saved ? 'delete' : 'post')
      .catch(() => flip(saved));
  }, [ids, navigate, signedIn]);

  const value = useMemo(() => ({ has: (id: string) => ids.has(id), toggle }), [ids, toggle]);
  return <FavouritesContext.Provider value={value}>{children}</FavouritesContext.Provider>;
}

function Heart({ organizationId, name }: { organizationId: string; name: string }) {
  const { t } = useTranslation();
  const favourites = useContext(FavouritesContext);
  if (!favourites) return null;
  const saved = favourites.has(organizationId);
  return <button type="button" className="hub-xcard-heart" aria-pressed={saved}
    aria-label={saved ? t('hub.explore.unsave', 'Remove {{name}} from favourites', { name }) : t('hub.explore.save', 'Save {{name}}', { name })}
    onClick={(event) => { event.preventDefault(); favourites.toggle(organizationId); }}>
    {saved ? <FavoriteIcon sx={{ fontSize: 20 }} aria-hidden /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} aria-hidden />}
  </button>;
}

function distanceLabel(km: number | null) {
  if (km == null) return null;
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}

export function BusinessCard({ card }: { card: OrganizationCard }) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const cover = card.cover?.cardUrl ?? card.coverUrl;
  const logo = card.logo?.thumbUrl ?? card.logoUrl;
  const price = card.minPrice != null ? t('hub.card.from', 'from {{price}}', { price: formatMoney(card.minPrice, card.currencyCode, lang) }) : null;
  const services = [card.hasOnlineBooking ? t('hub.card.bookOnline', 'Book online') : null, card.hasShop ? t('hub.card.shop', 'Shop') : null].filter(Boolean);
  const logoTile = <span className="hub-xcard-mark"><Photo src={logo ? platformHubUrl(logo) : null} fallback={initials(card.name)} /></span>;

  return <article className="hub-xcard">
    <div className="hub-xcard-media" data-group={card.groupCode ?? undefined}
      style={card.cover?.dominantColor ? { backgroundColor: card.cover.dominantColor } : undefined}>
      <Photo src={cover ? platformHubUrl(cover) : null} className="cover" fallback={logoTile} />
      {cover && logo && <span className="hub-xcard-logo"><Photo src={platformHubUrl(logo)} fallback={initials(card.name)} /></span>}
      {card.groupName && <span className="hub-xcard-badge">{card.groupName}</span>}
      <Heart organizationId={card.organizationId} name={card.name} />
    </div>
    <div className="hub-xcard-body">
      <div className="hub-xcard-title">
        <h3><Link to={companyPath(card.slug)} onClick={(event) => openCompany(event, card.slug)}>{card.name}</Link></h3>
        {card.isVerified && <VerifiedIcon className="hub-xcard-verified" titleAccess={t('hub.card.verified', 'Verified')} sx={{ fontSize: 16 }} />}
      </div>
      <span className="hub-xcard-meta">{[card.primaryCategory?.name, card.city, distanceLabel(card.distanceKm)].filter(Boolean).join(' · ')}</span>
      {(services.length > 0 || price) && <span className="hub-xcard-meta">{[...services, price].filter(Boolean).join(' · ')}</span>}
      {card.openJobs > 0 && <span className="hub-xcard-tag">{t('hub.card.jobs', '{{count}} open jobs', { count: card.openJobs })}</span>}
    </div>
  </article>;
}

export function ProductTile({ product }: { product: ProductCard }) {
  const lang = usePublicLanguage();
  const { t } = useTranslation();
  const { business } = product;
  const discounted = product.compareAtPrice != null && product.compareAtPrice > product.price;
  return <article className="hub-xcard product">
    <div className="hub-xcard-media" data-group={business.groupCode ?? undefined}>
      <Photo src={product.imageUrl} className="cover" fallback={<span className="hub-xcard-mark">{initials(product.name)}</span>} />
      {discounted && <span className="hub-xcard-badge sale">{t('hub.explore.sale', 'Sale')}</span>}
    </div>
    <div className="hub-xcard-body">
      <div className="hub-xcard-title"><h3><Link to={companyPath(business.slug)} onClick={(event) => openCompany(event, business.slug)}>{product.name}</Link></h3></div>
      <span className="hub-xcard-meta">{[business.name, business.city].filter(Boolean).join(' · ')}</span>
      <span className="hub-xcard-price">
        {formatMoney(product.price, product.currency, lang)}
        {discounted && <s>{formatMoney(product.compareAtPrice, product.currency, lang)}</s>}
      </span>
    </div>
  </article>;
}

function salaryLabel(job: JobCard, lang: string) {
  const min = formatMoney(job.salaryMin, job.salaryCurrency, lang);
  const max = formatMoney(job.salaryMax, job.salaryCurrency, lang);
  if (min && max) return `${min} – ${max}`;
  return min ?? max;
}

function postedLabel(publishedAt: string | null, lang: string) {
  if (!publishedAt) return null;
  const days = Math.floor((Date.now() - new Date(publishedAt).getTime()) / 86_400_000);
  if (!Number.isFinite(days)) return null;
  const format = new Intl.RelativeTimeFormat(lang === 'al' ? 'sq' : lang, { numeric: 'auto', style: 'short' });
  if (days < 7) return format.format(-Math.max(0, days), 'day');
  if (days < 30) return format.format(-Math.floor(days / 7), 'week');
  return format.format(-Math.floor(days / 30), 'month');
}

export function JobRow({ job }: { job: JobCard }) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const salary = salaryLabel(job, lang);
  const posted = postedLabel(job.publishedAt, lang);
  const isNew = job.publishedAt != null && Date.now() - new Date(job.publishedAt).getTime() < 3 * 86_400_000;
  return <article className="hub-xjob">
    <span className="hub-xjob-mark" aria-hidden>{initials(job.organizationName)}</span>
    <div className="hub-xjob-body">
      <h3><Link to={ROUTES.job.replace(':jobId', job.id)}>{job.title}</Link></h3>
      <span className="hub-xcard-meta">{[job.organizationName, job.city, employmentLabel(t, job.employmentType)].filter(Boolean).join(' · ')}</span>
      {salary && <span className="hub-xjob-salary">{salary}</span>}
    </div>
    <div className="hub-xjob-side">
      {isNew && <span className="hub-xjob-new">{t('hub.explore.new', 'New')}</span>}
      {posted && <span>{posted}</span>}
    </div>
  </article>;
}
